import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LoadingSpinner } from '../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Lock, BarChart3, CheckCircle, X } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

export function BidComparisonTab({ eventRef, sections }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sealed, setSealed] = useState(false);
  const [revealDate, setRevealDate] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/api/rfx/events/${eventRef}/responses`);
        if (r.data.sealed) {
          setSealed(true);
          setRevealDate(r.data.reveal_date || '');
        } else {
          setResponses(r.data.data || []);
        }
      } catch { toast.error('Failed to load responses'); }
      setLoading(false);
    };
    load();
  }, [eventRef]);

  const allQuestions = [];
  (sections || []).forEach((section, sIdx) => {
    (section.questions || []).forEach((q, qIdx) => {
      allQuestions.push({ ...q, section: section.section_name, sIdx, qIdx });
    });
  });

  const bestScores = {};
  allQuestions.forEach(q => {
    let best = -1;
    responses.forEach(r => {
      const ans = (r.answers || []).find(a => a.question_id === q.question_id);
      if (ans && ans.weighted_score > best) best = ans.weighted_score;
    });
    bestScores[q.question_id] = best;
  });

  const handleAdvance = async (responseId) => {
    try {
      const r = await api.post(`/api/rfx/events/${eventRef}/responses/${responseId}/advance`, {});
      toast.success(r.data.message);
      const res = await api.get(`/api/rfx/events/${eventRef}/responses`);
      setResponses(res.data.data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to advance')); }
  };

  const handleReject = async (responseId) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    const notify = window.confirm('Notify the supplier of this rejection?');
    try {
      await api.post(`/api/rfx/events/${eventRef}/responses/${responseId}/reject`, {
        reason, notify_supplier: notify,
      });
      toast.success('Response rejected');
      const res = await api.get(`/api/rfx/events/${eventRef}/responses`);
      setResponses(res.data.data || []);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to reject')); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>;

  if (sealed) {
    return (
      <div className="text-center py-12" data-testid="bids-sealed">
        <Lock className="w-10 h-10 mx-auto mb-3 text-amber-500" />
        <h3 className="text-lg font-semibold text-slate-700">Bids Are Sealed</h3>
        <p className="text-sm text-slate-500 mt-2">Supplier responses are sealed until the reveal date.</p>
        {revealDate && <p className="text-sm text-amber-600 font-medium mt-1">Reveal date: {revealDate.replace('T', ' at ').replace('Z', ' UTC')}</p>}
      </div>
    );
  }

  if (responses.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400" data-testid="bids-empty">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No supplier responses received yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2" data-testid="bid-comparison-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-700">{responses.length}</p>
          <p className="text-xs text-slate-500">Total Responses</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-700">
            {responses.length > 0 ? Math.max(...responses.map(r => r.percentage_score || 0)).toFixed(1) : 0}%
          </p>
          <p className="text-xs text-slate-500">Highest Score</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-slate-700">
            {responses.length > 0 ? (responses.reduce((s, r) => s + (r.percentage_score || 0), 0) / responses.length).toFixed(1) : 0}%
          </p>
          <p className="text-xs text-slate-500">Average Score</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-700">
            {responses.filter(r => r.status === 'SUBMITTED').length}
          </p>
          <p className="text-xs text-slate-500">Pending Review</p>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-100 border-b">
              <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-slate-100 min-w-[200px] z-10">Question</th>
              {responses.map(r => (
                <th key={r.response_id} className="px-3 py-2 text-center font-semibold min-w-[140px]">
                  <div>{r.supplier_name || r.supplier_id}</div>
                  <Badge className={`text-[9px] mt-1 ${r.status === 'REJECTED' ? 'bg-red-100 text-red-700' : r.status === 'SUBMITTED' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{r.status}</Badge>
                  <div className="text-indigo-700 font-bold text-sm mt-1">{(r.percentage_score || 0).toFixed(1)}%</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allQuestions.map((q, idx) => {
              const isNewSection = idx === 0 || q.section !== allQuestions[idx - 1]?.section;
              return (
                <React.Fragment key={q.question_id}>
                  {isNewSection && (
                    <tr className="bg-indigo-50"><td colSpan={responses.length + 1} className="px-3 py-1.5 font-semibold text-indigo-800">{q.section}</td></tr>
                  )}
                  <tr className="border-b hover:bg-slate-50">
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      <span className="font-medium">{q.text}</span>
                      {q.required && <span className="text-red-500 ml-0.5">*</span>}
                      <Badge className="ml-1.5 text-[8px] bg-slate-100 text-slate-500">{q.type}</Badge>
                    </td>
                    {responses.map(r => {
                      const ans = (r.answers || []).find(a => a.question_id === q.question_id);
                      const isBest = ans && ans.weighted_score === bestScores[q.question_id] && bestScores[q.question_id] > 0;
                      return (
                        <td key={r.response_id} className={`px-3 py-2 text-center ${isBest ? 'bg-emerald-50 font-semibold text-emerald-800' : ''}`}>
                          <div className="text-xs">{ans?.value || '—'}</div>
                          <div className="text-[10px] text-slate-400">{ans ? `${ans.score}/${ans.max_score}` : ''}</div>
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
            <tr className="bg-slate-100 border-t-2 font-bold">
              <td className="px-3 py-2 sticky left-0 bg-slate-100 z-10">Overall Score</td>
              {responses.map(r => (
                <td key={r.response_id} className="px-3 py-2 text-center text-indigo-700 text-sm">
                  {(r.percentage_score || 0).toFixed(1)}%
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Response Actions</h3>
        {responses.map(r => (
          <div key={r.response_id} className="flex items-center justify-between border rounded-lg px-4 py-2.5">
            <div>
              <span className="font-medium text-sm">{r.supplier_name || r.supplier_id}</span>
              <Badge className={`ml-2 text-[9px] ${r.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{r.status}</Badge>
              <span className="text-xs text-slate-400 ml-2">{r.current_stage ? `Stage: ${r.current_stage}` : ''}</span>
            </div>
            <div className="flex gap-2">
              {r.status !== 'REJECTED' && (
                <>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-300"
                    onClick={() => handleAdvance(r.response_id)} data-testid={`advance-${r.response_id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Advance
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-300"
                    onClick={() => handleReject(r.response_id)} data-testid={`reject-${r.response_id}`}>
                    <X className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
